// src/pages/LoginPage.tsx
//
// ★★ WAVE 6 — ANNEX A STORY 14. This screen now has TWO doors, and which one it
// offers is decided by the SERVER, not by anything written here.
//
// ══ WHAT WAS WRONG BEFORE, IN ONE SENTENCE ══
//
// `docs/delivery/DEMO-READINESS-PLAN.md` §2 item 1 put story 14 first in the
// order a customer meets this product and marked it NOT BUILT: "the federated
// OIDC adapter is real and *unreachable*: `LoginPage.tsx` never redirects to an
// authorization endpoint and never consumes `code`/`state`." Both halves were
// true. The adapter (`adapters/federated-oidc-identity-provider.ts`) had done
// authorization-code + PKCE + state + nonce + JWKS verification since Task 5,
// `GET /api/identities` had been returning an `authorizationUrl` for a federated
// build all along, and `POST /api/session` had accepted `{ code, state }` — and
// no screen used any of it.
//
// ══ THE TWO DOORS, AND WHY THE CLIENT DOES NOT CHOOSE ══
//
//   * **FEDERATED** — WOG ADFS / GCC Entra ID over OIDC (P2C3 §1.1.1(e)), with
//     SingPass or CorpPass 2FA where high identity assurance is needed
//     (P2C3 §10.11). Offered exactly when the response carries an
//     `authorizationUrl`; the browser is sent there, the provider authenticates
//     the officer and redirects back with `?code=&state=`, and this page redeems
//     them through the SAME `POST /api/session` the roster posts to.
//   * **THE SEEDED ROSTER** — a passwordless role chooser, offered exactly when
//     the response carries identities.
//
// ⚠ **`GET /api/identities` returns one shape or the other from a single branch
// on which provider `buildServer` composed.** So there is no client-side flag to
// keep in step and no way for this screen to disagree with the gateway about
// which mode it is in — the mistake that would otherwise show a customer a
// federated button a deployment cannot honour, or hide one it can.
//
// ⚠ **THE ROSTER IS NOW A STATED FALLBACK, not the only path** — and Wave 6's own
// acceptance line demanded the statement, not just the redirect. The plan says to
// DECIDE EXPLICITLY: "if the demonstration is a PC with no customer data, a
// seeded role chooser is defensible and should be SAID on screen." That decision
// is recorded (2026-08-19, Niger): the demonstration runs on a PC with no
// customer data. So the roster stays, and the screen says what it is, why it is
// acceptable here, and what a real deployment does instead — rather than letting
// a viewer infer that passwordless sign-in is the product.
//
// ══════════════════════════════════════════════════════════════════════════════
// ★★ WAVE 8 — THREE CHANGES, AND THE WAVE 6 GUARANTEE ABOVE IS UNCHANGED BY ALL
//    THREE. Read the ⚠ at line 29 first; every paragraph below is held to it.
// ══════════════════════════════════════════════════════════════════════════════
//
// **1. The federated button is now ALWAYS RENDERED, and DISABLED with a stated
// reason when the response carries no `authorizationUrl`.**
//
// ⚠ That is not the mistake line 29 forbids, and the distinction is exact. The
// forbidden thing is *offering a door a deployment cannot honour* — a control
// that looks live, is pressed, and either goes nowhere or pretends to
// authenticate. What is rendered here instead is a **statement in the shape of a
// button**: `disabled`, so it cannot be pressed at all, beside prose that says
// why. Nothing about which door WORKS moved to the client — `authorizationUrl`
// is still the whole and only test, still server-minted, still one branch in
// `buildServer`. What changed is that its ABSENCE is now said out loud rather
// than expressed by an absence a viewer has to notice.
//
// ⚠ The same house pattern `NotificationsPage` already applies to Run outbox:
// *"A disabled control with no explanation is the defect this wave keeps
// finding"*. The reason is rendered as ordinary prose beside the button, not only
// as a `title`, because a disabled control is not focusable and a tooltip nobody
// can reach is not an explanation.
//
// **2. The roster is a labelled DROPDOWN, and each option states the access
// boundary BEFORE sign-in.** Nine outline buttons made nine identities look
// interchangeable. The option text is `identity.label` — composed SERVER-SIDE by
// `services/gateway/src/identity-roster.ts` and rendered here verbatim.
//
// ⚠ **Nothing on this screen derives a clearance from raw grants.** Doing so
// would be S-011 — a second implementation of an authorisation rule this app does
// not own, drifting silently in the direction that makes the screen look right —
// and `published-bounds.test.ts` is the census that keeps that count at zero for
// the thirteen administration write forms. The one fallback below falls back to
// the EMAIL, which is data the response carried, never to a sentence this file
// assembled.
//
// ⚠ **The MACHINE IDENTITY is in the list, labelled, on purpose.**
// `prs.service@prs2.gov.sg.invalid` holds level-2 uncapped approval over six
// agencies while the only action it performs needs level 0 — `SECURITY.md`
// **S-070**, decided by **ADR 0012** (narrow it), with application deliberately
// deferred by the repository owner until after the demonstration so the product's
// own access-control matrix reports it live. Filtering it out of this dropdown
// would delete that finding and quietly undo a written decision.
//
// **3. The outcome is ANNOUNCED, and the field says which field.** `FormResult`
// puts every refusal in a live region (WCAG 2.2 3.3.1) and the select carries
// `aria-invalid` + a description of the problem.
//
// ⚠ **`SgdsSelect`'s OWN `invalid`/`hasFeedback`/`invalidFeedback` do that
// wiring, not the shared `FieldError`** — and this is a wiring fact, not a
// preference. `components/BoundedField.tsx`'s header states it for the whole app:
// *"`aria-describedby` has to name an element in the SAME document, and an SGDS
// component keeps its input inside a shadow root where an id in the light DOM
// cannot reach it"*. So a `FieldError` beside an `sgds-select` would be a
// paragraph near a box that the control does not reference — the exact
// unreferenced-message defect that component exists to prevent. SGDS renders
// `aria-invalid` and `aria-describedby` on its own internal input from these
// three props (`components/Select/sgds-select.js`), which is the design system
// doing what `fieldErrorProps` does for the native fields it can reach. SGDS is
// mandatory here (P2C1 §12.86(d)) and `SgdsSelect` is the control five other
// screens already use.
import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, type Location } from 'react-router'
import type { IdentityProviderReport, NotificationDeliveryReport, SettlementDispatchReport } from '@prs2/contracts'
import {
  SgdsAlert,
  SgdsButton,
  SgdsMasthead,
  SgdsSelect,
  SgdsSelectOption,
  SgdsSkeleton,
} from '@govtechsg/sgds-web-component/react'
import { GovFooter } from '../components/GovFooter'
import { api, ApiError, isNetworkError } from '../lib/api'
import { environmentBanner } from '../lib/environment'
import { targetValue } from '../lib/sgds-dom'
import type { DemoIdentity } from '../lib/types'
import { FormResult, type FormResultState } from '../components/FormResult'
import { AgencyMark } from '../components/AgencyMark'
import { useAuth } from '../state/AuthContext'
import { forgetReturnTo, peekReturnTo } from '../lib/sso-return'
import { useDocumentTitle } from '../lib/crumbs'

/**
 * The empty selection — a deliberate choice rather than a pre-picked officer.
 *
 * ⚠ It is what makes "nothing chosen" a REACHABLE state, and therefore what makes
 * the `aria-invalid` wiring and the live-region announcement below testable
 * rather than dead code. It also stops the first row of the roster from becoming
 * the accidental default: the row that sorts first today is an `HSP_USER`, but the
 * order is the seed's and nothing here should quietly inherit whatever it becomes.
 */
const NOTHING_SELECTED = ''

/** Said when Continue is pressed with no officer chosen. */
const CHOOSE_AN_OFFICER = 'Choose an officer from the list before continuing.'

/**
 * The option text for one identity.
 *
 * ⚠ `label` is declared `string` in `lib/types.ts` and still arrives over a
 * `JSON.parse` cast — `lib/api.ts`'s `requestSelfReporting` exists because a field
 * the server did not send is `undefined` however confidently a type declares it.
 * A gateway older than this bundle publishes no `label` at all, and an option
 * rendering `undefined` is a blank row nobody can identify.
 *
 * ⚠ **The fallback is the EMAIL and nothing else.** It is the address the officer
 * is about to sign in as — data the response certainly carried — and it states no
 * clearance. Falling back to a summary composed here would be exactly the S-011
 * second copy this whole mechanism removes.
 */
function optionLabel(identity: DemoIdentity): string {
  return typeof identity.label === 'string' && identity.label.length > 0 ? identity.label : identity.email
}

/** The network-failure sentence, in words an officer can act on (no "gateway"). */
const COULD_NOT_REACH_PRS = 'PRS could not be reached. Check your network connection and try again. If it keeps failing, contact PRS support.'

export function LoginPage() {
  useDocumentTitle('Sign in')
  const { status, user, login, completeFederatedLogin, sessionExpired } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [identities, setIdentities] = useState<readonly DemoIdentity[] | null>(null)
  // What the gateway reports about ITSELF, read from the same response the
  // identity roster already comes from — this page is signed out by
  // definition, so it cannot read either value from AuthContext (which only
  // has one once `/api/me`/`login()` has actually returned; see that file's
  // own comment). Both are `null` until the response lands, at which point
  // `environmentBanner` says nothing at all rather than claiming safety it has
  // no evidence for — the same rule Shell.tsx follows, from the same function.
  //
  // ⚠ `identityProvider` is the one that decides whether anything is shown.
  // This screen is where it matters most — it is the screen offering to sign
  // someone in as anyone with no password — and "a passwordless roster is
  // installed" is a fact about the provider, not about the deployment's name.
  const [environment, setEnvironment] = useState<string | null>(null)
  // ⚠ `IdentityProviderReport | null`, and the `null` is "the roster request has
  // not come back yet" — nothing else. A response that carried no provider is a
  // report, and it warns. See `requestSelfReporting` in `lib/api.ts`.
  const [identityProvider, setIdentityProvider] = useState<IdentityProviderReport | null>(null)
  // ⚠ The second witness, held here for the same reason the Shell holds it:
  // the notice's payment-agency clause is licensed by this field alone
  // (S-016), and `null` is "the roster request has not come back yet" — a
  // response carrying no value is a report of `null`, which WITHDRAWS the
  // clause rather than granting it. The two surfaces read the same three
  // fields through the same function; a badge or a sentence that appeared on
  // one screen and not the other is the defect class this app has hit twice.
  const [settlementDispatch, setSettlementDispatch] = useState<SettlementDispatchReport | null>(null)
  // ⚠★ THE THIRD WITNESS, added by the final integration review of the
  // 2026-08-15 notification-delivery wave. It licenses no reassurance at all —
  // only the `DEMO IDENTITIES WITH LIVE MAIL` alarm, which is precisely the
  // alarm this screen must raise BEFORE a one-click passwordless login is
  // taken, not after. Same `null` discipline as the two above.
  const [notificationDelivery, setNotificationDelivery] = useState<NotificationDeliveryReport | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  const [loginError, setLoginError] = useState<string | null>(null)
  /**
   * ★★ WAVE 8 — which officer the dropdown is on, `''` for none.
   *
   * Deliberately NOT initialised from the roster once it arrives: see
   * {@link NOTHING_SELECTED}.
   */
  const [selectedEmail, setSelectedEmail] = useState<string>(NOTHING_SELECTED)
  /**
   * ★★ WAVE 8 — the FIELD-level problem on the officer dropdown, or `null`.
   *
   * Two things reach it, and they are the two things that can be wrong about
   * this one field:
   *   · nothing was chosen when Continue was pressed;
   *   · the gateway REFUSED the address that was chosen (`POST /api/session`
   *     answers 401 for an address with no active `app.app_user` row), which is
   *     a fact about the field's value and not about the form in general.
   *
   * ⚠ It is set only by pressing Continue, never while the officer is still
   * choosing — a field that announces itself invalid before anybody has finished
   * interacting with it is the "premature validation" noise WCAG 3.3.1 is not
   * asking for.
   */
  const [officerProblem, setOfficerProblem] = useState<string | null>(null)
  /**
   * ★★ WAVE 8 — what the live region says, through the shared `FormResult`.
   *
   * ⚠ Held alongside `loginError` rather than replacing it: the `SgdsAlert` is
   * the VISIBLE treatment a sighted presenter reads at a glance, and this is the
   * ANNOUNCED one. `FormResult`'s own header records why the announcement cannot
   * be inferred from a paragraph appearing in the DOM.
   */
  const [outcome, setOutcome] = useState<FormResultState>({ status: 'idle' })
  /**
   * ★★ WAVE 6 — where to send the browser for a federated sign-in, or `null`.
   *
   * `undefined` on the response means this gateway composed the demo roster;
   * a string means it composed the federated provider. One branch, server-side.
   */
  const [authorizationUrl, setAuthorizationUrl] = useState<string | null>(null)
  /** Redeeming a `?code=&state=` callback. Distinct from `pendingEmail`: a different act. */
  const [completing, setCompleting] = useState(false)
  /**
   * ★ ADR 0015 — single sign-on succeeded and this person has no usable PRS account. Set from the gateway's one refusal that is
   * allowed to say why (`reason: 'NO_ACCOUNT'`), because the person has already authenticated.
   */
  const [noAccount, setNoAccount] = useState<{ readonly email: string } | null>(null)
  /** Whether this mount has already redeemed its callback — see the effect below. */
  const redeemedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    api
      .identities()
      .then((result) => {
        if (!cancelled) {
          setIdentities(result.identities)
          setAuthorizationUrl(result.authorizationUrl ?? null)
          setEnvironment(result.environment)
          setIdentityProvider(result.identityProvider)
          setSettlementDispatch(result.settlementDispatch)
          setNotificationDelivery(result.notificationDelivery)
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setLoadError(
          isNetworkError(error)
            ? COULD_NOT_REACH_PRS
            : error instanceof Error
              ? error.message
              : 'Could not load the list of officers.',
        )
      })
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * ★★ WAVE 6 — THE CALLBACK. The identity provider redirects the browser back
   * to this page with `?code=…&state=…`; this redeems them.
   *
   * ⚠ **The parameters are STRIPPED from the URL before the exchange is
   * attempted**, and that ordering is deliberate. An authorization code is
   * single-use: leaving it in the address bar invites a reload that replays a
   * code the gateway will refuse, and the officer reads that refusal as "sign-in
   * is broken" rather than "that code was already spent". It also keeps the code
   * out of anything that records a URL — history, a bookmark, a screenshot in a
   * support ticket.
   *
   * ⚠ **`replace`, never `push`**, so Back does not return to a URL carrying a
   * spent code.
   *
   * ⚠ **A failure says which act failed.** "Could not complete the sign-in
   * started with your identity provider" is a different sentence from a roster
   * refusal, because the officer's next action is different: try the provider
   * again, not pick a different identity.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const code = params.get('code')
    const state = params.get('state')
    if (code === null || state === null) return

    // ★★ AT MOST ONCE PER MOUNT, and this guard is a CORRECTNESS FIX rather than
    // a tidy-up.
    //
    // ⚠ An authorization code is SINGLE-USE. React's StrictMode double-invokes
    // effects, so without this the same code was redeemed twice: the first
    // exchange succeeded, the second was correctly refused by the gateway, and
    // the officer ended up looking at "that authorization code is not
    // redeemable" AFTER a sign-in that had actually worked. `test/dom.tsx`'s own
    // header says StrictMode is kept on precisely to catch a non-idempotent
    // effect — this is the one it caught.
    //
    // ⚠ A ref and not state: it must be set SYNCHRONOUSLY, before the second
    // invocation reads it. A `useState` update is not visible until the next
    // render, which is exactly too late.
    if (redeemedRef.current) return
    redeemedRef.current = true

    setCompleting(true)
    setLoginError(null)
    // Strip first — see this effect's own note on why the order matters.
    navigate(location.pathname, { replace: true })
    void completeFederatedLogin(code, state)
      .then(() => {
        navigate('/dashboard', { replace: true })
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.reason === 'NO_ACCOUNT') {
          setNoAccount({ email: typeof error.detail['email'] === 'string' ? error.detail['email'] : '' })
          return
        }
        setLoginError(
          error instanceof ApiError
            ? error.message
            : isNetworkError(error)
              ? COULD_NOT_REACH_PRS
              : 'Could not complete the sign-in started with your identity provider. Start again: each sign-in link can only be used once.',
        )
      })
      .finally(() => {
        setCompleting(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The remembered return path has done its job once somebody is signed in; clear it so a later sign-in does not inherit it.
  useEffect(() => {
    if (status === 'signed-in') forgetReturnTo()
  }, [status])

  if (status === 'signed-in') {
    /**
     * `location.state.from` is the deep-link feature: open `/bills/X` while signed
     * out, get bounced here by `RequireAuth`, sign in, land on `/bills/X`.
     *
     * ★★★ **S-079 — WHY THERE IS NO SIGN-OUT CHECK HERE, and there was one for
     * about an hour.** `RequireAuth` used to capture `from` on ANY transition to
     * `signed-out`, including a deliberate `logout()`, so this line sent the NEXT
     * officer to sign in straight to the page the PREVIOUS one was on. The first fix
     * was a `signedOutDeliberately` test right here — and it was a **no-op**:
     * `adopt()` clears that flag in the same commit that sets `signed-in`, so by the
     * time this branch runs the flag is already false. The browser journey failed
     * identically, which is what showed it.
     *
     * So the gate lives at the point of CAPTURE — `RequireAuth` does not put `from`
     * into the state at all for a deliberate sign-out. Nothing needs checking here,
     * because there is nothing to ignore. See `RequireAuth.tsx` for the argument and
     * `LoginPage.redirect.dom.test.tsx` for both halves (the leak closed, the deep
     * link still working).
     *
     * ⚠ A guard here would now be a line that looks like a control and is not, which
     * is worse than no line at all — this repository logs that shape repeatedly.
     */
    const from = (location.state as { from?: Location } | null)?.from ?? undefined
    // ★ ADR 0015 — a System Administrator lands on the page that chooses who to act as; everyone else goes where they were going
    // (the page remembered before single sign-on took them away, then a deep link, then the dashboard).
    return <Navigate to={user?.canSwitchUser === true ? '/assume' : (peekReturnTo() ?? from?.pathname ?? '/dashboard')} replace />
  }

  /**
   * ★★ WAVE 8 — sign in as the officer the dropdown is on.
   *
   * ⚠ **The refusal lands in THREE places, and each answers a different
   * question.** `officerProblem` marks the FIELD (`aria-invalid` on the select,
   * with the reason described by it); `outcome` ANNOUNCES it in the live region;
   * `loginError` is the visible `SgdsAlert` a sighted presenter reads at a glance.
   * A refusal that only painted the alert is the WCAG 2.2 3.3.1 failure
   * `FormResult` exists to close, and one that only announced would leave the
   * field looking valid.
   */
  const handleContinue = async () => {
    if (selectedEmail === NOTHING_SELECTED) {
      // ⚠ Not a disabled button. `FieldError.tsx`'s header measures why: a form
      // that arrives at a disabled submit with NOTHING said is a worse 3.3.1
      // failure than the opaque line it was written to remove.
      setOfficerProblem(CHOOSE_AN_OFFICER)
      setOutcome({ status: 'error', message: CHOOSE_AN_OFFICER })
      setLoginError(null)
      return
    }

    setOfficerProblem(null)
    setLoginError(null)
    setOutcome({ status: 'submitting' })
    setPendingEmail(selectedEmail)
    try {
      await login(selectedEmail)
      navigate('/dashboard', { replace: true })
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : isNetworkError(error)
            ? COULD_NOT_REACH_PRS
            : 'Could not sign in. Try again, and contact PRS support if it keeps failing.'
      // ⚠ A 401 is a fact about the ADDRESS that was chosen — `POST /api/session`
      // answers it when no active `app.app_user` row holds that email — so it
      // marks the field as well as the form. Anything else (the gateway is down,
      // an unexpected shape) is not about this field and must not claim to be.
      setOfficerProblem(error instanceof ApiError && error.status === 401 ? message : null)
      setLoginError(message)
      setOutcome({ status: 'error', message })
    } finally {
      setPendingEmail(null)
    }
  }

  /**
   * ★ F-01: the officer list opens on Enter and Space as well as the arrow keys. SGDS's select input swallows every
   * key except Tab and Arrow Up/Down, so Enter and Space did nothing and the field could not be opened with the
   * two keys a keyboard user presses on any other control. Only a key pressed ON the closed field is handled: Enter
   * on an option inside the open list still chooses that option.
   */
  const openOnEnterOrSpace = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    if (event.target !== event.currentTarget) return
    const host = event.currentTarget as HTMLElement & { menuIsOpen?: boolean; showMenu?: () => unknown }
    if (host.menuIsOpen === true || typeof host.showMenu !== 'function') return
    event.preventDefault()
    void host.showMenu()
  }

  // One function decides what this build may say about itself, here and in the
  // Shell — see lib/environment.ts (I8).
  const banner = environmentBanner(identityProvider, settlementDispatch, environment, notificationDelivery)
  const federated = authorizationUrl !== null
  const selected = identities?.find((identity) => identity.email === selectedEmail)

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#login-main" className="pt-skip-link">
        Skip to sign in
      </a>
      {/*
        ⚠ THE MASTHEAD WAS MISSING FROM THIS SCREEN, and this is the screen where it
        matters most. SGDS requires the Official Government Banner at the top of every
        page, ahead of branding, on every flow — and the sign-in screen is where a visitor
        decides whether the site asking them to identify themselves is really government,
        which is precisely what a phishing page wants them not to check. `Shell` carried
        it and this page did not, so the one page outside the shell was the one page
        without it.
      */}
      <SgdsMasthead fluid={false} />

      <header className="pt-brandband">
        {/* ★ 2026-08-26 — the Authority's mark on the sign-in band too, the same
            component as the Shell's, by the owner's direction: the first screen
            a presenter shows carried the only band without it. */}
        <AgencyMark />
        <span className="pt-wordmark">PRS2.0</span>
        <span className="pt-divider" aria-hidden="true" />
        <span className="text-sm opacity-90">Public Service Division</span>
        {banner.badge !== null && <span className="pt-env-badge">{banner.badge}</span>}
      </header>

      {/*
        Deliberately asymmetric with the Shell, which renders BOTH notices.
        The demo-identity warning belongs after sign-in, where the Shell already
        shows it above every screen carrying data. The DANGER notices have to
        appear here, before an identity is chosen: this screen is where a
        passwordless one-click login is offered, and the moment to say "this may
        not be a demo" — or "this build cannot tell you your actions stop short
        of a live payment agency" — is before it is used, not after.
      */}
      {banner.notice !== null && banner.notice.variant === 'danger' && (
        <SgdsAlert show variant="danger" title={banner.notice.title}>
          {banner.notice.body}
        </SgdsAlert>
      )}

      <main id="login-main" tabIndex={-1} className="flex-1 grid place-items-center px-6 py-10">
        <div className="w-full max-w-xl">
          <h1 className="text-3xl mb-2">Sign in</h1>

          {noAccount !== null && (
            <div className="pt-card mb-6 flex flex-col gap-3" role="alert">
              <h2 className="text-xl">No access yet</h2>
              <p className="m-0 text-[var(--sgds-body-color-subtle)]">
                You signed in through single sign-on{noAccount.email.length > 0 ? <> as <span className="pt-mono">{noAccount.email}</span></> : null},
                but you do not have an active PRS account yet. Ask a System Administrator to add you, then sign in again.
              </p>
              <div>
                <SgdsButton
                  variant="outline"
                  type="button"
                  disabled={authorizationUrl === null}
                  onClick={() => {
                    // Ask the identity provider to offer its account picker, so a person with two accounts can choose the other one.
                    if (authorizationUrl !== null) window.location.href = `${authorizationUrl}&prompt=select_account`
                  }}
                >
                  Sign in with a different account
                </SgdsButton>
              </div>
            </div>
          )}

          {/*
            ★★★ WAVE 27 — WHY THE OFFICER IS BACK HERE, when they did not ask to be.
            The gateway holds sessions in memory, so restarting it ends every open
            session. Before this, each screen reported that separately — the
            dashboard alone showed five "Could not load … a session is required"
            cards, each with a Retry that could never succeed. The officer was
            reading a pile of breakages instead of one fact.
            ⚠ It renders ONLY for an expiry, never on a first visit or a deliberate
            sign-out: `sessionExpired` is not the negation of
            `signedOutDeliberately` — both are false when nobody has signed in yet,
            and telling a first-time visitor their session ended is a small lie.
          */}
          {sessionExpired && (
            <div className="pt-card mb-6" role="status">
              <p className="font-semibold">Your session ended, so you were signed out.</p>
              <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
                Sign in again to carry on: nothing you had open was lost, and you will be returned to the page you
                were on. Sessions end whenever the service restarts.
              </p>
            </div>
          )}

          {/*
            ★★ THE FEDERATED DOOR — ALWAYS RENDERED, and live exactly when the
            gateway reports an `authorizationUrl`.

            See this file's Wave 8 note 1: the server still decides which door
            WORKS, and the disabled state is a statement rather than a door. It
            cannot be pressed at all, and the reason sits beside it in prose
            because a disabled control is not focusable and a tooltip nobody can
            reach is not an explanation.
          */}
          <div className="pt-card flex flex-col gap-4 mb-6">
            <div>
              <h2 className="text-xl mb-1">Single sign-on with your government identity</h2>
              <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
                Government sign-in (WOG ADFS or GCC Entra ID), with SingPass or CorpPass two-factor where high
                identity assurance is required. Intranet only. PRS holds no password of yours and never sees one.
              </p>
              {!federated && (
                <p className="text-sm mt-2 max-w-[70ch] text-[var(--sgds-body-color-subtle)]">
                  <strong>Unavailable on this deployment.</strong> There is no identity provider to redirect to, so
                  this button does nothing rather than pretending to authenticate. It goes live as soon as a
                  deployment connects one.
                </p>
              )}
            </div>
            <div>
              <SgdsButton
                variant="primary"
                type="button"
                disabled={!federated || completing}
                loading={completing}
                onClick={() => {
                  // ⚠ Guarded, and not only by `disabled`. A disabled SGDS button
                  // is the CONTROL's refusal; this is the HANDLER's, so a
                  // programmatic click (an extension, a future refactor that
                  // drops the prop) still cannot navigate to `null`.
                  //
                  // ⚠ Measured 2026-08-20: SGDS reflects `disabled` onto its host
                  // element and, with that attribute present, a `click()` on the
                  // host reaches this handler ZERO times — so no DOM test can
                  // exercise this line while the button is correctly disabled.
                  // `LoginPage.dom.test.tsx` therefore states that the pair is
                  // only JOINTLY falsifiable, and proves it by planting both.
                  if (authorizationUrl === null) return
                  // ⚠ A whole-page navigation, not a fetch. The provider
                  // authenticates a PERSON in a browser — it may show a
                  // consent screen, a 2FA prompt, an existing session — and
                  // none of that can happen inside an XHR. The URL is opaque
                  // and server-minted; nothing here assembles it.
                  window.location.href = authorizationUrl
                }}
              >
                {completing ? 'Completing sign-in…' : 'Continue to your identity provider'}
              </SgdsButton>
            </div>
          </div>

          {/*
            ★★ THE ROSTER, and what it now SAYS about itself.
            Wave 6's acceptance line asked for the decision to be made explicitly
            and said on screen — not merely for the redirect to exist.
          */}
          {!federated ? (
            <>
              <h2 className="text-xl mb-1">Demonstration sign-in</h2>
              {/*
                ★ 2026-08-26 — ONE sentence, by the owner's direction: the
                presenter says the rest aloud. Two things still have to be on
                the screen itself, and both are pinned by tests — the absence
                of a provider (`LoginPage.dom.test.tsx`, `journeys.spec.ts`)
                and the no-customer-data ground the decision rests on. The
                federated path is described once already, in the card above.
              */}
              <p className="text-[var(--sgds-body-color-subtle)] mb-4 max-w-[70ch]">
                Choose the identity to sign in as. <strong>This deployment has no federated identity provider
                configured</strong>, so a seeded roster with <strong>no customer data</strong> stands in:
                nothing here authenticates a person.
              </p>
            </>
          ) : (
            <p className="text-sm text-[var(--sgds-color-subtle)] mb-6 max-w-[70ch]">
              This deployment federates identity, so no roster is offered here. Which bills, agencies and
              administration surfaces you may reach is decided by your grants in PRS, not by the identity
              provider: the same database-driven authorisation either way.
            </p>
          )}

          {loginError !== null && (
            <div className="mb-4">
              <SgdsAlert show variant="danger" title="Sign-in failed">
                {loginError}
              </SgdsAlert>
            </div>
          )}

          {loadError !== null && (
            <div className="pt-card" role="alert">
              <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">Could not load the list of officers</p>
              <p className="mt-2 text-[var(--sgds-body-color-subtle)]">{loadError}</p>
              <p className="mt-2 text-sm text-[var(--sgds-color-subtle)]">Reload this page to try again.</p>
            </div>
          )}

          {loadError === null && identities === null && (
            <div className="pt-card flex flex-col gap-3" aria-busy="true">
              <span className="sr-only">Loading identities…</span>
              {[0, 1, 2].map((i) => (
                <SgdsSkeleton key={i} height="52px" borderRadius="10px" />
              ))}
            </div>
          )}

          {identities !== null && identities.length > 0 && (
            /*
             * ★★ ONE LABELLED DROPDOWN, replacing nine outline buttons.
             *
             * The option text is `identity.label`, composed SERVER-SIDE
             * (`services/gateway/src/identity-roster.ts`) and rendered verbatim:
             * `Name — Role · agencies · bill types · approval authority`, with
             * `MACHINE IDENTITY ·` leading the machine caller's. That puts the
             * access boundary in front of the reader BEFORE the choice, which is
             * the whole point — nine buttons reading only a role label made nine
             * genuinely different clearances look interchangeable.
             *
             * ⚠ The seeded `displayName` is back on screen, inside the label,
             * where the machine identity's old name/role collision cannot recur:
             * `ROLE_LABELS.PRS_SERVICE` is "PRS Service (machine)" and Task 5g
             * gave that account the distinct display name "PRS Batch Recompute
             * Service" precisely so the two read as a name and a function rather
             * than the same string twice.
             */
            <div className="pt-card flex flex-col gap-3">
              <SgdsSelect
                label="Officer"
                placeholder="Choose an officer"
                value={selectedEmail}
                onKeyDown={openOnEnterOrSpace}
                invalid={officerProblem !== null}
                hasFeedback
                invalidFeedback={officerProblem ?? ''}
                disabled={pendingEmail !== null}
                onSgdsChange={(e) => {
                  setSelectedEmail(targetValue(e))
                  // ⚠ Clearing the field problem on change, not on submit: the
                  // officer has acted on the thing that was wrong, and leaving
                  // `aria-invalid` set would report a field that is no longer
                  // in the state the message describes.
                  setOfficerProblem(null)
                }}
              >
                <SgdsSelectOption value={NOTHING_SELECTED}>Choose an officer&hellip;</SgdsSelectOption>
                {identities.map((identity) => (
                  <SgdsSelectOption key={identity.email} value={identity.email}>
                    {optionLabel(identity)}
                  </SgdsSelectOption>
                ))}
              </SgdsSelect>

              {/*
                ★★ THE EXPLANATORY COPY, and the sentence an evaluator will test.

                ⚠ It says the grants are resolved AT SIGN-IN, not on every
                request, because that is what the code does and the difference is
                material. `resolveLocalUserByEmail` (`services/gateway/src/identity.ts`)
                SELECTs `app.user_scope` once and `establishSession`
                (`routes/identity.ts`) stores the resulting `Scope` on the session;
                `server.ts`'s `onRequest` hook then only re-reads that stored
                session, never the table. `settlement-batches.test.ts`'s own
                `beforeAll` carries the warning this produced in practice —
                "a session's Scope is resolved ONCE at login from `app.user_scope`
                as it stood then. Signing in before the grants below exist hands
                the session the OLD grant list". A screen claiming the clearance
                is read fresh on every request would be describing a system this
                is not.
              */}
              <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch] m-0">
                Signing in as different officers is how access is shown: ask the same question as two officers
                and the answers differ, because each officer can reach different things.{' '}
                <strong>The list chooses who you are, never what you may do.</strong> What each officer may see is
                set in PRS, read once when the officer signs in, and held for the whole session. Every later
                request is filtered by it, so nothing you pick here can widen it.
              </p>

              {selected !== undefined && (
                <p className="text-sm text-[var(--sgds-color-subtle)] m-0">
                  You will sign in as <span className="pt-mono">{selected.email}</span>.
                </p>
              )}

              <div>
                <SgdsButton
                  variant="primary"
                  type="button"
                  loading={pendingEmail !== null}
                  disabled={pendingEmail !== null}
                  onClick={() => void handleContinue()}
                >
                  {pendingEmail !== null ? 'Signing in…' : 'Continue as selected officer'}
                </SgdsButton>
              </div>

              {/*
                The ANNOUNCED half of every outcome above. Always mounted, so the
                live region exists before its text does — see `FormResult`'s own
                header for why that ordering is the whole mechanism.
              */}
              <FormResult state={outcome} />
            </div>
          )}

          {/*
            ⚠ A roster that came back EMPTY is not a roster that has not arrived.
            A federated deployment publishes `identities: []` deliberately (the
            route's own comment says so), and this screen must not render an empty
            dropdown labelled "Officer" beside the sentence saying no roster is
            offered. The two states are distinguished rather than collapsed.
          */}
          {identities !== null && identities.length === 0 && !federated && (
            <div className="pt-card" role="alert">
              <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">No identities to choose from</p>
              <p className="mt-2 text-[var(--sgds-body-color-subtle)]">
                This gateway reports a passwordless roster and published none, so there is nothing to sign in
                as. Seed the database with <code className="pt-mono">pnpm db:migrate</code> and reload.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* SGDS: Privacy and Terms on every page, this one included. */}
      <GovFooter />
    </div>
  )
}
